import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import HomepageBanner from "@/components/HomepageBanner";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import SnowEffect from "@/components/SnowEffect";
import LeavesEffect from "@/components/LeavesEffect";
import ConfettiEffect from "@/components/ConfettiEffect";
import { supabase } from "@/integrations/supabase/client";
import { Shield, Users, FileCheck, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SeasonalSettings {
  snow_enabled: boolean;
  snow_intensity: 'light' | 'medium' | 'heavy';
  leaves_enabled: boolean;
  leaves_intensity: 'light' | 'medium' | 'heavy';
  confetti_enabled: boolean;
  confetti_intensity: 'light' | 'medium' | 'heavy';
}

const Index = () => {
  const [seasonalSettings, setSeasonalSettings] = useState<SeasonalSettings | null>(null);

  useEffect(() => {
    const loadSeasonalSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'seasonal_effects')
          .maybeSingle();

        if (!error && data?.setting_value) {
          setSeasonalSettings(data.setting_value as unknown as SeasonalSettings);
        }
      } catch (error) {
        console.error('Error loading seasonal settings:', error);
      }
    };

    loadSeasonalSettings();
  }, []);

  const featuredServices = [
    { icon: FileCheck, label: "Cyber Essentials" },
    { icon: Users, label: "Fractional CISO" },
    { icon: Shield, label: "Risk Management" },
    { icon: AlertTriangle, label: "Incident Response" },
  ];

  return (
    <div className="min-h-screen">
      {seasonalSettings?.snow_enabled && (
        <SnowEffect intensity={seasonalSettings.snow_intensity} />
      )}
      {seasonalSettings?.leaves_enabled && (
        <LeavesEffect intensity={seasonalSettings.leaves_intensity} />
      )}
      {seasonalSettings?.confetti_enabled && (
        <ConfettiEffect intensity={seasonalSettings.confetti_intensity} />
      )}
      <SEO
        title="Cyber Essentials Certification UK | Expert Cyber Security Services | 4FOX Security"
        description="Get Cyber Essentials certified with 4FOX Security. UK's trusted certification body partner with 98% pass rate. Expert cyber security services including fractional CISO, risk management & 24/7 emergency response for UK businesses."
        canonical="https://4fox.uk/"
        keywords={["Cyber Essentials certification UK", "Cyber Essentials Plus", "UK cyber security", "Cyber Essentials assessor", "IASME certification", "fractional CISO UK", "cyber security consultancy UK"]}
        localAreas={["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Organisation",
          "name": "4FOX Security",
          "url": "https://4fox.uk",
          "logo": "https://4fox.uk/4fox-logo-optimized.png",
          "description": "UK Cyber Essentials certification partner and expert cyber security consultancy. Helping UK businesses achieve Cyber Essentials and Cyber Essentials Plus certification.",
          "email": "hello@4fox.uk",
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "State",
              "name": "Somerset"
            },
            {
              "@type": "State",
              "name": "North Somerset"
            },
            {
              "@type": "City",
              "name": "Weston-super-Mare"
            },
            {
              "@type": "City",
              "name": "Bristol"
            },
            {
              "@type": "State",
              "name": "South Gloucestershire"
            }
          ],
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "GB"
          },
          "sameAs": ["https://www.linkedin.com/company/4foxsecurity/"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Cyber Security Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cyber Essentials Certification",
                  "description": "UK Government-backed cyber security certification for businesses of all sizes"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cyber Essentials Plus Certification",
                  "description": "Enhanced Cyber Essentials certification with technical verification"
                }
              }
            ]
          }
        }}
      />
      <Navbar />
      <Hero />
      <HomepageBanner />
      
      {/* Service Areas Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Serving Somerset, Bristol & Beyond
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Based in Weston-super-Mare, we provide expert cyber security services to businesses across 
              <span className="font-semibold text-foreground"> Somerset, North Somerset, Bristol, and South Gloucestershire</span>, 
              with nationwide coverage throughout the United Kingdom.
            </p>
            <p className="text-muted-foreground">
              Whether you're a local SME in the South West or a growing business anywhere in the UK, 
              we deliver the same high-quality fractional CISO and cyber security expertise.
            </p>
          </div>
        </div>
      </section>
      
      {/* Compact Services CTA Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Cyber Security Services
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              From Cyber Essentials certification to fractional CISO support, we provide comprehensive 
              security services tailored to UK businesses.
            </p>
            
            {/* Featured service icons */}
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              {featuredServices.map((service) => (
                <div 
                  key={service.label}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-sm font-medium">{service.label}</span>
                </div>
              ))}
            </div>
            
            <Button asChild size="lg" className="group">
              <Link to="/services">
                View All Services
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Index;
